<script setup lang="ts">
import type { INewsDataCategoriesClientDTO } from '~/shared/api/client/news/news.dto';

const { $viewport } = useNuxtApp();

const props = defineProps<{
  categories: INewsDataCategoriesClientDTO[];
  selected: number;
}>();

const emit = defineEmits<{
  (e: 'category-selected', id: number): void;
}>();

const handleClick = (tab: INewsDataCategoriesClientDTO) => {
  emit('category-selected', tab.id);
};

const sortedTabsList = computed<INewsDataCategoriesClientDTO[]>(() => {
  const allTab: INewsDataCategoriesClientDTO = {
    id: 0,
    name: 'Все',
    status: 'active',
  };

  // The partner tag always comes first (right after "Все")
  const partnerTab = props.categories.find((c) => c.is_partner);
  const rest = props.categories.filter((c) => !c.is_partner);
  const head = partnerTab ? [allTab, partnerTab] : [allTab];

  const selectedId = props.selected;

  if (selectedId === 0 || selectedId === partnerTab?.id) {
    return [...head, ...rest];
  }

  const selectedCategory = rest.find((c) => c.id === selectedId);
  const filtered = rest.filter((c) => c.id !== selectedId);

  return selectedCategory
    ? [...head, selectedCategory, ...filtered]
    : [...head, ...filtered];
});

const style = useCssModule();
const container = ref<HTMLDivElement>();

const suitableTabs = ref<INewsDataCategoriesClientDTO[]>(sortedTabsList.value);
const isShowSwitchButton = ref(false);
const isShowAllTabs = ref(false);
let tabsContainerWidth = 0;

const displayedTabs = computed(() =>
  isShowAllTabs.value ? sortedTabsList.value : suitableTabs.value,
);

const isTouchDevice = () =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0;

const measureTextWidth = (cssClass: string, text: string): number => {
  const span = document.createElement('span');
  span.style.position = 'absolute';
  span.style.visibility = 'hidden';
  span.style.whiteSpace = 'nowrap';
  span.classList.add(cssClass);
  span.textContent = text;
  document.body.appendChild(span);
  const width = span.offsetWidth;
  document.body.removeChild(span);
  return width;
};

const calculateFittingTabs = () => {
  const switchButtonWidth = measureTextWidth(
    style.tabs_button ?? '',
    'Все вкладки',
  );
  const tabs = sortedTabsList.value;
  const fitting: INewsDataCategoriesClientDTO[] = [];
  let accumulated = 0;

  for (const tab of tabs) {
    const tabWidth = measureTextWidth(style.tabs_tab ?? '', tab.name);
    // резервируем место под кнопку, если не все табы поместятся
    const remaining = tabs.length - fitting.length - 1;
    const needButton = remaining > 0;
    const limit = needButton
      ? tabsContainerWidth - switchButtonWidth
      : tabsContainerWidth;

    if (accumulated + tabWidth <= limit) {
      fitting.push(tab);
      accumulated += tabWidth;
    } else {
      // убираем последний вмещавшийся если нужно место под кнопку
      while (
        fitting.length &&
        accumulated + switchButtonWidth > tabsContainerWidth
      ) {
        const removed = fitting.pop()!;
        accumulated -= measureTextWidth(style.tabs_tab ?? '', removed.name);
      }
      break;
    }
  }

  suitableTabs.value = fitting;
  isShowSwitchButton.value = fitting.length < tabs.length;
};

let resizeObserver: ResizeObserver | null = null;

const createResizeObserver = () => {
  const el = container.value;
  if (!el) return;
  resizeObserver = new ResizeObserver(() => {
    tabsContainerWidth = el.offsetWidth;
    calculateFittingTabs();
  });
  resizeObserver.observe(el);
};

const removeResizeObserver = () => {
  resizeObserver?.disconnect();
  resizeObserver = null;
};

watch($viewport.breakpoint, () => {
  if ($viewport.isLessOrEquals('mobile')) {
    removeResizeObserver();
  } else if (!resizeObserver) {
    createResizeObserver();
  }
});

// Drag-to-scroll
const isDragging = ref(false);
let dragStartX = 0;
let dragScrollLeft = 0;

const onMouseDown = (e: MouseEvent) => {
  const el = container.value;
  if (!el) return;
  isDragging.value = true;
  dragStartX = e.pageX - el.offsetLeft;
  dragScrollLeft = el.scrollLeft;
};

const onMouseMove = (e: MouseEvent) => {
  const el = container.value;
  if (!el || !isDragging.value) return;
  e.preventDefault();
  el.scrollLeft = dragScrollLeft - (e.pageX - el.offsetLeft - dragStartX) * 3;
};

const onMouseUp = () => {
  isDragging.value = false;
};
const onMouseLeave = () => {
  isDragging.value = false;
};

onMounted(() => {
  if (!$viewport.isLessOrEquals('mobile')) {
    createResizeObserver();
  }

  if (!isTouchDevice()) {
    const el = container.value;
    if (!el) return;
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mousemove', onMouseMove);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mouseleave', onMouseLeave);
  }
});

onBeforeUnmount(() => {
  removeResizeObserver();

  if (!isTouchDevice()) {
    const el = container.value;
    if (!el) return;
    el.removeEventListener('mousedown', onMouseDown);
    el.removeEventListener('mousemove', onMouseMove);
    el.removeEventListener('mouseup', onMouseUp);
    el.removeEventListener('mouseleave', onMouseLeave);
  }
});
</script>

<template>
  <div :class="$style.tabs">
    <div ref="container" :class="$style.tabs__container">
      <div
        v-for="tab in displayedTabs"
        :key="tab.id"
        :class="[
          $style.tabs_tab,
          {
            [$style.tabs_tab_active!]: tab.id === selected,
          },
        ]"
        @click="handleClick(tab)"
      >
        {{ tab.name }}
      </div>
      <div
        v-if="isShowSwitchButton"
        id="switchButton"
        :class="$style.tabs_button"
        @click="isShowAllTabs = !isShowAllTabs"
      >
        {{ isShowAllTabs ? 'Скрыть' : 'Все вкладки' }}
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.tabs {
  &__container {
    display: flex;
    align-items: center;
    overflow-x: auto;
    scrollbar-width: none;
    min-height: 10px;
    border-bottom: 1px solid var(--color-gray-line);
    padding-left: var(--space-2-5x);
    padding-right: var(--space-2-5x);
  }

  &_tab {
    padding: var(--space-2-5x) var(--space-2x);
    @include text_body2_medium;
    color: var(--color-black-1);
    cursor: pointer;
    border-bottom: 3px solid transparent;
    transition: all 0.2s ease;
    flex-shrink: 0;
    &_active {
      color: var(--color-red-1);
      border-bottom: 3px solid var(--color-red-1);
    }
  }

  &_tab:hover {
    color: var(--color-red-1);
    border-bottom: 3px solid var(--color-red-1);
  }

  &_button {
    padding: 7px var(--space-2-5x);
    @include text_caption1_medium;
    color: var(--color-red-1);
    white-space: nowrap;
    border-radius: var(--space-2-5x);
    background: var(--color-red-3);
    height: fit-content;
    transition: all 0.2s ease;
    cursor: pointer;
    margin-left: auto;
  }

  &_button:hover {
    background: #ffe5e7;
  }
}

@media (max-width: 767px) {
  .tabs {
    &__container {
      border-top: none;
    }
  }
}

@media (max-width: 479px) {
  .tabs {
    &__container {
      padding-left: var(--space-2x);
      padding-right: var(--space-2x);
    }
  }
}
</style>
